// Admin-only "Sys Health": the Rust lane on rst drawn live. One poll of /api/admin/rust/syshealth every 2 s
// feeds the map and the drilldown for the selected node. Nothing here changes state; StreamRift mode does.

import { useCallback, useState } from 'react';
import { AlertTriangle, Activity } from 'lucide-react';
import { useVisibilityAwarePoll } from '@/hooks/useVisibilityAwarePoll';
import { rustLane, fmt, type SysHealth } from '@/lib/rustLane/api';
import { SysHealthMap, type NodeId } from './map';
import { Drilldown } from './drilldown';

const POLL_MS = 2000;

export default function SysHealthView() {
  const [snap, setSnap] = useState<SysHealth | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<NodeId | null>('engine');

  const poll = useCallback(async (signal: AbortSignal) => {
    try {
      const s = await rustLane.syshealth(signal);
      if (signal.aborted) return;
      setSnap(s);
      setErr(null);
    } catch (e) {
      if (signal.aborted) return;
      setErr(e instanceof Error ? e.message : 'failed');
    }
  }, []);
  useVisibilityAwarePoll(poll, POLL_MS);

  // The relay caches the fan-out for a second; this is that cache's age, stamped server-side.
  const age = snap ? Math.max(0, snap.serverTs - snap.at) : null;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Activity className="text-muted-foreground h-5 w-5" />
            <h1 className="text-xl font-bold">Sys Health</h1>
            <span className="text-muted-foreground text-sm">the Rust lane on rst, live</span>
          </div>
          <div className="text-muted-foreground font-mono text-xs">
            {snap ? `snapshot ${age !== null ? fmt.age(age) : '—'} old · rsh ${snap.rsh.filter((b) => b.ok).length}/${snap.rsh.length} up · feedgw ${snap.feedgw.ok ? 'up' : 'down'} · engine ${snap.engine.ok ? 'up' : 'down'}` : 'loading…'}
          </div>
        </header>

        {err && (
          <div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-2.5 text-sm text-red-400">
            <AlertTriangle className="h-4 w-4" /> {err}
          </div>
        )}

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_400px]">
          <section className="border-border overflow-x-auto rounded-lg border p-3">
            <SysHealthMap snap={snap} selected={selected} onSelect={setSelected} />
          </section>
          <section className="border-border rounded-lg border p-4">
            <Drilldown snap={snap} node={selected} />
          </section>
        </div>
      </div>
    </div>
  );
}
